// Author: Ahsan Saleem
/**
 * Local offline stores (plan §11.2). Dexie wraps IndexedDB for structured
 * offline data. Persistence is best-effort, so the server sync and a
 * user-visible outbox are product requirements, not implementation detail
 * (plan §11.4).
 */
import Dexie, { type Table } from 'dexie';

export interface OutboxItem {
  localId?: number;
  /** What the mutation is, for display in the sync queue. */
  label: string;
  method: 'POST';
  path: string;
  body: unknown;
  /** The idempotency key sent to the server so a retry is exactly-once. */
  idempotencyKey: string;
  createdAt: string;
  state: 'pending' | 'syncing' | 'failed';
  attempts: number;
  lastError?: string;
}

export interface SyncState {
  key: string;
  lastSuccessAt?: string;
  lastError?: string;
}

export interface CachedTask {
  id: string;
  title: string;
  category: string;
  state: string;
  zone_id: string | null;
  batch_id: string | null;
  requires_evidence: string;
  due_at: string;
  origin_alert_id: string | null;
  /** Set locally once queued/completed so the UI reflects it before sync. */
  localState?: string;
}

class BloomosDb extends Dexie {
  outbox!: Table<OutboxItem, number>;
  syncState!: Table<SyncState, string>;
  tasks!: Table<CachedTask, string>;

  constructor() {
    super('bloomos-ops');
    this.version(1).stores({
      outbox: '++localId, state, idempotencyKey',
      syncState: 'key',
      tasks: 'id, state, due_at',
    });
  }
}

export const db = new BloomosDb();

export async function getSyncState(): Promise<SyncState> {
  return (await db.syncState.get('main')) ?? { key: 'main' };
}

export async function setSyncState(patch: Partial<SyncState>): Promise<void> {
  const cur = await getSyncState();
  await db.syncState.put({ ...cur, ...patch, key: 'main' });
}
